// file-uploader.ts
import * as Sentry from "@sentry/nextjs";
import { useEffect, useRef, useState } from "react";
import { useS3Upload, S3UploadError } from "ui/lib/useS3Upload";
import { createContainer } from "unstated-next";

interface CurrentUpload {
  id: string;
  newUploadData: any;
  clearUploadOnFinish: boolean;
  percentage: number;
  completed?: boolean;
  failed?: boolean;
  error?: string;
}

type UploadsState = {
  [key: string]: CurrentUpload;
};

interface NewUploadData {
  id: string;
  file: File;
  bucket: string; // Allow specifying the bucket
  prefix?: string;
  clearUploadOnFinish?: boolean;
}

// Marker helpers moved to ui/lib/error-marks (a plain module with no React
// imports) so server code — API routes, apollo's reportError — can use them
// without pulling this client container into a server bundle. Imported and
// re-exported here for the existing client-side import sites.
import {
  asUserFacing,
  isUserFacingError,
  markSentryReported,
  markUserNotified,
  wasSentryReported,
  wasUserNotified,
} from "ui/lib/error-marks";

export {
  asUserFacing,
  isUserFacingError,
  markSentryReported,
  markUserNotified,
  wasSentryReported,
  wasUserNotified,
};

const isHeic = (file: File) => {
  return (
    file.type === "image/heic" || file.name.toLowerCase().endsWith(".heic")
  );
};

const useContainer = () => {
  const { upload } = useS3Upload();
  const [currentUploads, setCurrentUploads] = useState<UploadsState>({});
  // Keyed by upload id. A single shared ref used to hold only the most recent
  // timer, which leaked every earlier one on unmount and — worse — let a stale
  // timer from a previous upload delete a LIVE entry when the same id is
  // re-used (callers like the announcement poster upload under a constant id).
  // Deleting a live entry drops it from `hasActiveUploads` and re-enables send
  // mid-upload.
  const clearTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map()
  );

  useEffect(() => {
    const timers = clearTimers.current;
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  const cancelPendingClear = (id: string) => {
    const timer = clearTimers.current.get(id);
    if (!timer) return;
    clearTimeout(timer);
    clearTimers.current.delete(id);
  };

  const getProgress = (progress: { loaded: number; total: number }): number => {
    return Math.floor((progress.loaded * 100) / progress.total);
  };

  const handleClearUploadFromState = (id: string) => {
    if (!id) return;
    cancelPendingClear(id);
    clearTimers.current.set(
      id,
      setTimeout(() => {
        clearTimers.current.delete(id);
        setCurrentUploads((current) => {
          const { [id]: _, ...uploads } = current;
          return uploads;
        });
      }, 5000)
    );
  };

  /**
   * Drop an upload from state immediately, unlike `handleClearUploadFromState`
   * which waits 5s so finished uploads can show their "done" state. Used when
   * the user cancels: `hasActiveUploads` counts entries without `completed`, so
   * leaving one behind silently disables send with nothing on screen to explain
   * it.
   */
  const removeUploadFromState = (id: string) => {
    if (!id) return;
    cancelPendingClear(id);
    setCurrentUploads((current) => {
      const { [id]: _, ...uploads } = current;
      return uploads;
    });
  };

  // These three run against an upload that may already have been cancelled.
  // Spreading `current[id]` when the key is gone would resurrect the entry
  // WITHOUT `completed` — and since progress fires continuously during the PUT,
  // a cancelled upload would immediately re-orphan itself and disable send.
  // Bail instead of recreating what the user removed.
  const handleUploadFinished = (id: string) => {
    setCurrentUploads((current) => {
      if (!current[id]) return current;
      return {
        ...current,
        [id]: { ...current[id], percentage: 100, completed: true },
      };
    });
  };

  const handleUploadProgress =
    (id: string) => (progress: { loaded: number; total: number }) => {
      const percentage = getProgress(progress);
      setCurrentUploads((current) => {
        if (!current[id]) return current;
        return { ...current, [id]: { ...current[id], percentage } };
      });
    };

  const handleNewUpload = async (
    data: NewUploadData
  ): Promise<{ key: string; url: string }> => {
    if (!data.id) {
      throw new Error("id is required");
    }

    if (data.file && isHeic(data.file)) {
      throw asUserFacing(
        new Error(
          "HEIC files are not supported. Please convert to JPEG or PNG."
        )
      );
    }

    try {
      // A pending 5s clear from a previous upload under this id would otherwise
      // fire mid-upload and delete the entry we are about to create.
      cancelPendingClear(data.id);
      setCurrentUploads((current) => ({
        ...current,
        [data.id]: {
          id: data.id,
          newUploadData: data,
          clearUploadOnFinish: data.clearUploadOnFinish || false,
          percentage: 0,
        },
      }));

      const uploadResponse = await upload(
        {
          file: data.file,
          prefix: data.prefix,
          bucket: data.bucket,
        },
        handleUploadProgress(data.id)
      );

      if (!uploadResponse) throw new Error("upload failed");
      handleUploadFinished(data.id);

      if (data.clearUploadOnFinish) {
        handleClearUploadFromState(data.id);
      }

      return uploadResponse;
    } catch (error) {
      // Mark upload as failed in state (unless it was cancelled — see above)
      setCurrentUploads((current) => {
        if (!current[data.id]) return current;
        return {
          ...current,
          [data.id]: {
            ...current[data.id],
            percentage: 0,
            failed: true,
            error: error instanceof Error ? error.message : String(error),
          },
        };
      });

      handleClearUploadFromState(data.id);

      // Error is already being reported by useS3Upload, so we don't need to capture it again
      // Just rethrow with additional context about the upload attempt
      if (error instanceof Error) {
        error.message = `File upload failed (id: ${data.id}): ${error.message}`;
        // useS3Upload already captured this one; flag it so downstream handlers
        // don't report a second Sentry event for the same failure.
        markSentryReported(error);
      }

      throw error;
    }
  };

  return {
    currentUploads,
    setCurrentUploads,
    handleNewUpload,
    handleClearUploadFromState,
    removeUploadFromState,
    handleUploadFinished,
    handleUploadProgress,
  };
};

// we use unstated-next as a react context alternative
export const { useContainer: useFileUploader, Provider: FileUploaderProvider } =
  createContainer(useContainer);
